import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { navigation } from '@/config/navigation';
import { ChevronLeft, ChevronRight, Menu, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useGlobalStore } from '@/lib/store';
import { useMediaQuery } from '@/lib/hooks/use-media-query';
import { useClerk } from "@clerk/clerk-react";
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../theme-provider';
import { usePermission } from '@/lib/contexts/permission-context';
import LogoDark from '@/img/logo-dark.png';
import LogoLight from '@/img/logo-light.png';

export function Sidebar() {
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useClerk();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { sidebarOpen, setSidebarOpen } = useGlobalStore();
  const { theme } = useTheme();
  const { canAccess, loading: permissionsLoading } = usePermission();

  const [systemTheme, setSystemTheme] = useState<'dark' | 'light'>('light');

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    setSystemTheme(media.matches ? 'dark' : 'light');
    const listener = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light');
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, []);

  const resolvedTheme = theme === 'system' ? systemTheme : theme;

  const handleSignOut = () => {
    // Ensure redirect even if signOut throws due to expired session
    Promise.resolve()
      .then(() => signOut())
      .catch(() => { })
      .finally(() => navigate("/"));
  };

  // check theme
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile, setSidebarOpen]);

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev =>
      prev.includes(groupName)
        ? prev.filter(name => name !== groupName)
        : [...prev, groupName]
    );
  };

  // Filter navigation items based on permissions
  const filteredNavigation = navigation.filter(item => {
    // Always show dashboard and settings
    if (item.href === '/' || item.href === '/settings') return true;

    // Hide items while permissions are loading
    if (permissionsLoading) return false;

    // Check if user has permission to access this resource
    if (item.resource) {
      return canAccess(item.resource);
    }

    // If no resource specified, show the item (fallback)
    return true;
  }).map(item => {
    // Filter children based on permissions
    if (item.children) {
      return {
        ...item,
        children: item.children.filter(child => {
          if (child.resource) {
            return canAccess(child.resource);
          }
          return true;
        })
      };
    }
    return item;
  });

  return (
    <>
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 bg-card border-r border-border transition-all duration-300 ease-in-out",
          sidebarOpen ? "w-64" : "w-20",
          isMobile && !sidebarOpen && "-translate-x-full"
        )}
      >
        <div className="flex h-16 items-center justify-between px-4 border-b border-border bg-background">
          {sidebarOpen ? (
            <Link to="/" className="flex items-center space-x-2">
              <img
                src={resolvedTheme === 'dark' ? LogoLight : LogoDark}
                alt="Logo"
                className="h-16 w-60 my-2"
              />
            </Link>
          ) : (
            <div className="w-8 h-8" /> // Placeholder for spacing
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:flex text-foreground hover:text-foreground-subtle"
          >
            {sidebarOpen ?
              <ChevronLeft className="h-4 w-4" /> :
              <ChevronRight className="h-4 w-4" />
            }
          </Button>
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(false)}
              className="text-foreground hover:text-foreground-subtle"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="flex flex-col h-[calc(100%-4rem)]">
          <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-1">
            {filteredNavigation.map((item) => {
              const isActive = item.href === location.pathname;
              const isExpanded = expandedGroups.includes(item.name);

              if (item.children) {
                return (
                  <div key={item.name}>
                    <Button
                      variant="ghost"
                      className={cn(
                        "w-full text-foreground hover:text-foreground-subtle hover:bg-background-subtle",
                        sidebarOpen ? "justify-between" : "justify-center px-2",
                        isExpanded && "bg-background-subtle"
                      )}
                      onClick={() => sidebarOpen && toggleGroup(item.name)}
                    >
                      <div className={cn(
                        "flex items-center",
                        !sidebarOpen && "justify-center"
                      )}>
                        <item.icon className={cn(
                          "h-4 w-4",
                          sidebarOpen && "mr-3"
                        )} />
                        {sidebarOpen && <span>{item.name}</span>}
                      </div>
                      {sidebarOpen && (
                        <ChevronLeft className={cn(
                          "h-4 w-4 transition-transform",
                          isExpanded && "rotate-180"
                        )} />
                      )}
                    </Button>
                    {sidebarOpen && isExpanded && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className={cn(
                              "flex items-center px-4 py-2 text-sm rounded-md text-foreground hover:text-foreground-subtle hover:bg-background-subtle",
                              child.href === location.pathname && "bg-background-subtle text-primary"
                            )}
                          >
                            <child.icon className="h-4 w-4 mr-3" />
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "flex items-center rounded-md text-foreground hover:text-foreground-subtle hover:bg-background-subtle transition-all duration-200",
                    !sidebarOpen ? "px-2 py-2 justify-center" : "px-4 py-2",
                    isActive && "bg-primary/10 text-primary border-l-4 border-primary"
                  )}
                >
                  <item.icon className={cn(
                    "h-4 w-4",
                    sidebarOpen && "mr-3"
                  )} />
                  {sidebarOpen && <span>{item.name}</span>}
                </Link>
              );
            })}
          </nav>

          <div className={cn(
            "flex-shrink-0 p-4 border-t border-border",
            !sidebarOpen && "flex justify-center"
          )}>
            <Button
              variant="ghost"
              className={cn(
                "w-full text-foreground hover:text-foreground-subtle hover:bg-background-subtle",
                sidebarOpen ? "justify-start" : "justify-center px-2"
              )}
              onClick={handleSignOut}
            >
              <LogOut className={cn(
                "h-4 w-4",
                sidebarOpen && "mr-3"
              )} />
              {sidebarOpen && <span>Sign Out</span>}
            </Button>
          </div>
        </div>
      </div>

      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-background/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );
}